import { supabase } from '../lib/supabase';

export const payrollService = {
  async getAllRuns() {
    const { data, error } = await supabase?.from('payroll_runs')?.select('*')?.order('pay_period_start', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      paymentDate: row?.payment_date,
      payrollStatus: row?.payroll_status,
      totalGross: row?.total_gross,
      totalNet: row?.total_net,
      totalSwt: row?.total_swt,
      totalNasfund: row?.total_nasfund,
      processedBy: row?.processed_by,
      processedAt: row?.processed_at,
      createdAt: row?.created_at
    })) || [];
  },

  async getRunDetails(payrollRunId) {
    const { data, error } = await supabase?.from('payroll_details')?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)?.eq('payroll_run_id', payrollRunId);
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      basePay: row?.base_pay,
      overtimePay: row?.overtime_pay,
      allowances: row?.allowances,
      deductions: row?.deductions,
      grossPay: row?.gross_pay,
      swtAmount: row?.swt_amount,
      employeeSuper: row?.employee_super,
      employerSuper: row?.employer_super,
      netPay: row?.net_pay,
      hoursWorked: row?.hours_worked,
      overtimeHours: row?.overtime_hours
    })) || [];
  },

  async getSWTFilings() {
    const { data, error } = await supabase?.from('swt_filings')?.select('*')?.order('due_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      filingPeriod: row?.filing_period,
      dueDate: row?.due_date,
      filingStatus: row?.filing_status,
      totalSwt: row?.total_swt,
      submissionDate: row?.submission_date,
      paymentReference: row?.payment_reference,
      notes: row?.notes,
      createdAt: row?.created_at
    })) || [];
  },

  async getNasfundContributions() {
    const { data, error } = await supabase?.from('nasfund_contributions')?.select('*')?.order('due_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      contributionPeriod: row?.contribution_period,
      dueDate: row?.due_date,
      filingStatus: row?.filing_status,
      totalEmployeeContribution: row?.total_employee_contribution,
      totalEmployerContribution: row?.total_employer_contribution,
      submissionDate: row?.submission_date,
      paymentReference: row?.payment_reference,
      notes: row?.notes,
      createdAt: row?.created_at
    })) || [];
  },

  async updateSWTFiling(id, filing) {
    const { data, error } = await supabase?.from('swt_filings')?.update({
        filing_status: filing?.filingStatus,
        submission_date: filing?.submissionDate,
        payment_reference: filing?.paymentReference,
        notes: filing?.notes
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      filingStatus: data?.filing_status,
      submissionDate: data?.submission_date,
      paymentReference: data?.payment_reference
    };
  },

  async updateNasfundContribution(id, contribution) {
    const { data, error } = await supabase?.from('nasfund_contributions')?.update({
        filing_status: contribution?.filingStatus,
        submission_date: contribution?.submissionDate,
        payment_reference: contribution?.paymentReference,
        notes: contribution?.notes
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      filingStatus: data?.filing_status,
      submissionDate: data?.submission_date,
      paymentReference: data?.payment_reference
    };
  },

  async createPayrollEntry(entry) {
    const { data, error } = await supabase?.from('payroll_entries')?.insert({
      employee_id: entry?.employeeId,
      entry_type: entry?.entryType,
      entry_date: entry?.date,
      entry_category: entry?.type,
      units: entry?.units,
      rate: entry?.rate,
      value: entry?.value,
      notes: entry?.notes,
      created_at: new Date()?.toISOString()
    })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      employeeId: data?.employee_id,
      entryType: data?.entry_type,
      date: data?.entry_date,
      type: data?.entry_category,
      units: data?.units,
      rate: data?.rate,
      value: data?.value,
      notes: data?.notes,
      createdAt: data?.created_at
    };
  },

  async getMinimumWageRates() {
    const { data, error } = await supabase?.from('minimum_wage_rates')?.select('*')?.order('effective_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      effectiveDate: row?.effective_date,
      hourlyRate: row?.hourly_rate,
      determinationReference: row?.determination_reference,
      notes: row?.notes,
      isCurrent: row?.is_current,
      createdAt: row?.created_at
    })) || [];
  },

  async getCurrentMinimumWage() {
    const { data, error } = await supabase?.from('minimum_wage_rates')?.select('*')?.eq('is_current', true)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      effectiveDate: data?.effective_date,
      hourlyRate: data?.hourly_rate,
      determinationReference: data?.determination_reference,
      notes: data?.notes
    };
  }
};

export default payrollService;